/**
 * BranchZonesPage
 * Reuses ZonesPage logic but scoped to a specific branch via makeBranchApi.
 */
import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { makeBranchApi } from "../../api/branchApi";
import {
  PageHeader, Btn, EmptyState, LoadingSpinner,
  ConfirmModal, FormField, inputStyle, selectStyle,
} from "../../components/ui";
import Modal from "../../components/ui/Modal";
import toast from "react-hot-toast";

export default function BranchZonesPage() {
  const { branchId } = useParams();
  const api = makeBranchApi(branchId);

  const [zones,    setZones]   = useState([]);
  const [stations, setStations]= useState([]);
  const [loading,  setLoading] = useState(true);
  const [expanded, setExpanded]= useState({});

  const [zoneModal,    setZoneModal]    = useState(null);
  const [stationModal, setStationModal] = useState(null);
  const [counterModal, setCounterModal] = useState(null);
  const [outcomeModal, setOutcomeModal] = useState(null);
  const [deleteConfirm,setDeleteConfirm]= useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [zr, sr] = await Promise.all([api.listZones(), api.listStations()]);
      const zoneList = zr.data?.results || zr.data;
      setZones(zoneList);
      setStations(sr.data?.results || sr.data);
      const allExpanded = {};
      zoneList.forEach(z => { allExpanded[z.id] = true; });
      setExpanded(allExpanded);
    } catch { toast.error("Failed to load zones."); }
    finally  { setLoading(false); }
  }, [branchId]);

  useEffect(() => { load(); }, [load]);

  const stationsForZone = (zoneId) => stations.filter(s => s.zone === zoneId || s.zone_id === zoneId);

  const saveZone = async (form) => {
    setSaving(true);
    try {
      if (form.id) await api.updateZone(form.id, form);
      else         await api.createZone(form);
      toast.success(`Zone ${form.id ? "updated" : "created"}.`);
      setZoneModal(null); load();
    } catch (e) {
      toast.error(e.response?.data?.name?.[0] || "Failed to save zone.");
    } finally { setSaving(false); }
  };

  const saveStation = async (form) => {
    setSaving(true);
    try {
      if (form.id) await api.updateStation(form.id, form);
      else         await api.createStation(form);
      toast.success(`Station ${form.id ? "updated" : "created"}.`);
      setStationModal(null); load();
    } catch (e) {
      toast.error(e.response?.data?.name?.[0] || "Failed to save station.");
    } finally { setSaving(false); }
  };

  const saveCounter = async (form) => {
    setSaving(true);
    try {
      if (form.id) await api.updateCounter(form.id, form);
      else         await api.createCounter(form);
      toast.success(`Counter ${form.id ? "updated" : "created"}.`);
      setCounterModal(null); load();
    } catch { toast.error("Failed to save counter."); }
    finally  { setSaving(false); }
  };

  const saveOutcome = async (form) => {
    setSaving(true);
    try {
      if (form.id) await api.updateOutcome(form.id, form);
      else         await api.createOutcome(form);
      toast.success("Outcome saved.");
      setOutcomeModal(null); load();
    } catch { toast.error("Failed to save outcome."); }
    finally  { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setSaving(true);
    try {
      const { type, id } = deleteConfirm;
      if (type === "zone")    await api.deleteZone(id);
      if (type === "station") await api.deleteStation(id);
      if (type === "counter") await api.deleteCounter(id);
      if (type === "outcome") await api.deleteOutcome(id);
      toast.success(`${type} deleted.`);
      setDeleteConfirm(null); load();
    } catch { toast.error("Failed to delete."); }
    finally  { setSaving(false); }
  };

  const toggleZone = (id) => setExpanded(p => ({ ...p, [id]: !p[id] }));

  if (loading) return <div style={{ padding: "32px" }}><LoadingSpinner /></div>;

  return (
    <div style={styles.root}>
      <PageHeader
        title="Zones & Stations"
        subtitle="Define zones, service stations, counters, and routing outcomes."
        action={<Btn onClick={() => setZoneModal({})}>+ New Zone</Btn>}
      />

      {zones.length === 0 ? (
        <EmptyState icon="🗂️" title="No zones yet"
          subtitle="Create your first zone to start setting up this branch."
          action={<Btn onClick={() => setZoneModal({})}>+ New Zone</Btn>}
        />
      ) : zones.map(zone => (
        <div key={zone.id} style={styles.zoneCard}>
          <div style={styles.zoneHeader}>
            <div style={styles.zoneLeft}>
              <button onClick={() => toggleZone(zone.id)} style={styles.expandBtn}>
                {expanded[zone.id] ? "▾" : "▸"}
              </button>
              <span style={styles.zoneName}>{zone.name}</span>
              {zone.is_main && <span style={styles.mainTag}>Main Block</span>}
              <span style={styles.stationCount}>{stationsForZone(zone.id).length} station{stationsForZone(zone.id).length !== 1 ? "s" : ""}</span>
            </div>
            <div style={styles.zoneActions}>
              <Btn size="sm" variant="ghost" onClick={() => setStationModal({ zone: zone.id })}>+ Station</Btn>
              <Btn size="sm" variant="ghost" onClick={() => setZoneModal(zone)}>Edit</Btn>
              <Btn size="sm" variant="danger" onClick={() => setDeleteConfirm({ type: "zone", id: zone.id, name: zone.name })}>Delete</Btn>
            </div>
          </div>

          {expanded[zone.id] && (
            <div style={styles.stationList}>
              {stationsForZone(zone.id).length === 0 ? (
                <div style={styles.emptyStations}>No stations in this zone yet.</div>
              ) : stationsForZone(zone.id).map(station => (
                <div key={station.id} style={styles.stationRow}>
                  <div style={styles.stationAvatar}>{station.name?.[0]?.toUpperCase() || "S"}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={styles.stationName}>
                      {station.name}
                      {station.is_entry_point && <span style={styles.entryTag}>Entry Point</span>}
                    </div>
                    <div style={styles.stationMeta}>
                      {(station.counter_count ?? 0)} counters · {(station.outcome_count ?? 0)} outcome{station.outcome_count !== 1 ? "s" : ""}
                    </div>
                  </div>
                  <div style={styles.stationActions}>
                    <Btn size="sm" variant="ghost" onClick={() => setCounterModal({ station: station.id })}>+ Counter</Btn>
                    <Btn size="sm" variant="ghost" onClick={() => setOutcomeModal({ station: station.id })}>+ Outcome</Btn>
                    <Btn size="sm" variant="ghost" onClick={() => setStationModal({ ...station, zone: station.zone || station.zone_id })}>Edit</Btn>
                    <Btn size="sm" variant="danger" onClick={() => setDeleteConfirm({ type: "station", id: station.id, name: station.name })}>Delete</Btn>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      {/* Zone Modal */}
      <ZoneModal open={!!zoneModal} zone={zoneModal} onClose={() => setZoneModal(null)} onSave={saveZone} saving={saving} />

      {/* Station Modal */}
      <StationModal open={!!stationModal} station={stationModal} zones={zones} onClose={() => setStationModal(null)} onSave={saveStation} saving={saving} />

      {/* Counter Modal */}
      <Modal open={!!counterModal} onClose={() => setCounterModal(null)} title="Add Counter" width="400px">
        <CounterForm form={counterModal || {}} onSave={saveCounter} onClose={() => setCounterModal(null)} saving={saving} />
      </Modal>

      {/* Outcome Modal */}
      <Modal open={!!outcomeModal} onClose={() => setOutcomeModal(null)} title="Add Outcome" width="400px">
        <OutcomeForm form={outcomeModal || {}} stations={stations} onSave={saveOutcome} onClose={() => setOutcomeModal(null)} saving={saving} />
      </Modal>

      <ConfirmModal
        open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}
        onConfirm={handleDelete} loading={saving}
        title={`Delete ${deleteConfirm?.type}?`}
        message={`"${deleteConfirm?.name}" will be permanently deleted.`}
        confirmLabel="Delete" variant="danger"
      />
    </div>
  );
}

function ZoneModal({ open, zone, onClose, onSave, saving }) {
  const [form, setForm] = useState({});
  useEffect(() => { if (open) setForm(zone || {}); }, [open, zone]);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  return (
    <Modal open={open} onClose={onClose} title={form.id ? "Edit Zone" : "New Zone"} width="420px">
      <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
        <FormField label="Zone Name" required>
          <input style={inputStyle} value={form.name || ""} onChange={e => set("name", e.target.value)} placeholder="e.g. Block A" />
        </FormField>
        <FormField label="Description">
          <input style={inputStyle} value={form.description || ""} onChange={e => set("description", e.target.value)} placeholder="Optional" />
        </FormField>
        <label style={{ display: "flex", gap: "8px", cursor: "pointer", fontSize: "13px", color: "var(--text-primary)" }}>
          <input type="checkbox" checked={!!form.is_main} onChange={e => set("is_main", e.target.checked)} />
          Mark as main block
        </label>
        <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn onClick={() => onSave(form)} disabled={saving || !form.name?.trim()}>{saving ? "Saving…" : "Save Zone"}</Btn>
        </div>
      </div>
    </Modal>
  );
}

function StationModal({ open, station, zones, onClose, onSave, saving }) {
  const [form, setForm] = useState({});
  useEffect(() => { if (open) setForm(station || {}); }, [open, station]);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  return (
    <Modal open={open} onClose={onClose} title={form.id ? "Edit Station" : "New Station"} width="420px">
      <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
        <FormField label="Station Name" required>
          <input style={inputStyle} value={form.name || ""} onChange={e => set("name", e.target.value)} placeholder="e.g. Customer Care" />
        </FormField>
        <FormField label="Zone" required>
          <select style={selectStyle} value={form.zone || ""} onChange={e => set("zone", e.target.value)}>
            <option value="">Select zone…</option>
            {zones.map(z => <option key={z.id} value={z.id}>{z.name}</option>)}
          </select>
        </FormField>
        <label style={{ display: "flex", gap: "8px", cursor: "pointer", fontSize: "13px", color: "var(--text-primary)" }}>
          <input type="checkbox" checked={!!form.is_entry_point} onChange={e => set("is_entry_point", e.target.checked)} />
          Entry point (issues tickets)
        </label>
        <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn onClick={() => onSave(form)} disabled={saving || !form.name?.trim() || !form.zone}>{saving ? "Saving…" : "Save Station"}</Btn>
        </div>
      </div>
    </Modal>
  );
}

function CounterForm({ form, onSave, onClose, saving }) {
  const [name, setName] = useState(form.name || "");
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
      <FormField label="Counter Name">
        <input style={inputStyle} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Counter 1" />
      </FormField>
      <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn onClick={() => onSave({ ...form, name })} disabled={saving}>{saving ? "Saving…" : "Add Counter"}</Btn>
      </div>
    </div>
  );
}

function OutcomeForm({ form, stations, onSave, onClose, saving }) {
  const [f, setF] = useState({ label: "", next_station: "", ...form });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
      <FormField label="Outcome Label" required>
        <input style={inputStyle} value={f.label} onChange={e => set("label", e.target.value)} placeholder="e.g. Refer to Lab" />
      </FormField>
      <FormField label="Next Station (optional)">
        <select style={selectStyle} value={f.next_station || ""} onChange={e => set("next_station", e.target.value)}>
          <option value="">None — end journey</option>
          {stations.filter(s => s.id !== form.station).map(s =>
            <option key={s.id} value={s.id}>{s.name}</option>
          )}
        </select>
      </FormField>
      <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn onClick={() => onSave(f)} disabled={saving || !f.label.trim()}>{saving ? "Saving…" : "Add Outcome"}</Btn>
      </div>
    </div>
  );
}

const styles = {
  root:          { padding: "32px", fontFamily: "'DM Sans','Segoe UI',sans-serif", maxWidth: "1000px" },
  zoneCard:      { background: "var(--surface-bg)", border: "1px solid var(--surface-border)", borderRadius: "14px", marginBottom: "16px", overflow: "hidden" },
  zoneHeader:    { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", gap: "12px" },
  zoneLeft:      { display: "flex", alignItems: "center", gap: "10px", flex: 1, minWidth: 0 },
  expandBtn:     { background: "none", border: "none", color: "#60A5FA", cursor: "pointer", fontSize: "16px", padding: "0 4px", fontFamily: "inherit" },
  zoneName:      { fontSize: "15px", fontWeight: "700", color: "var(--text-primary)" },
  mainTag:       { fontSize: "10px", background: "var(--surface-border)", color: "#60A5FA", border: "1px solid rgba(37,99,235,0.25)", borderRadius: "4px", padding: "2px 6px", fontWeight: "600" },
  stationCount:  { fontSize: "12px", color: "var(--text-muted)" },
  zoneActions:   { display: "flex", gap: "6px", flexShrink: 0 },
  stationList:   { borderTop: "1px solid rgba(37,99,235,0.08)", padding: "12px 20px 16px" },
  emptyStations: { color: "var(--text-muted)", fontSize: "13px", padding: "8px 0" },
  stationRow:    { display: "flex", alignItems: "center", gap: "12px", padding: "10px 14px", background: "var(--input-bg)", borderRadius: "10px", marginBottom: "8px" },
  stationAvatar: { width: "32px", height: "32px", borderRadius: "8px", background: "linear-gradient(135deg,#7C3AED,#2563EB)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "13px", fontWeight: "700", color: "#fff", flexShrink: 0 },
  stationName:   { fontSize: "14px", fontWeight: "600", color: "var(--text-primary)", display: "flex", alignItems: "center", gap: "6px" },
  entryTag:      { fontSize: "10px", background: "rgba(34,197,94,0.12)", color: "#22C55E", border: "1px solid rgba(34,197,94,0.2)", borderRadius: "4px", padding: "2px 6px", fontWeight: "600" },
  stationMeta:   { fontSize: "12px", color: "var(--text-muted)", marginTop: "2px" },
  stationActions:{ display: "flex", gap: "6px", flexShrink: 0 },
};
